import React from 'react'
import { List, useTable, DeleteButton, DateField } from '@refinedev/antd'
import { useList, type CrudFilter } from '@refinedev/core'
import { useSearchParams, Link } from 'react-router'
import { Alert, Image, Space, Table } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { renderAvatarName } from '../../theme/columns'
import { ListPage, Pill, TableSurface } from '../../theme/listkit'

type MemoryComment = Tables<'memory_comments'>
type Photo = Tables<'photos'>
type Group = Tables<'groups'>
type Profile = Tables<'profiles'>

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The comment id from `?id=`, or null. A value that isn't a uuid is ignored (it would 400 in PostgREST). */
export function parseCommentIdParam(raw: string | null | undefined): string | null {
  const v = (raw ?? '').trim()
  return UUID_RE.test(v) ? v : null
}

/** Link to this page filtered to one comment — used by the Reports page. */
export function memoryCommentPath(id: string): string {
  return `/memory_comments?id=${encodeURIComponent(id)}`
}

/**
 * Memory Comments — every comment on a Classroom memory
 * (public.memory_comments, Classrooms Phase 1, migration
 * 20261016120200_classrooms_social.sql), newest first.
 *
 * READ AND DELETE ONLY. The table has no update policy — a comment is never
 * rewritten, by its author or by staff — so there is no Create or Edit here.
 * Delete is the admin branch of the table's delete policy
 * (`user_id = auth.uid() or public.is_admin()`), and the row is audited
 * (admin_audit_enable(..., 'admin_only')), so a removal shows in Audit Trail.
 * Members report a comment from the app as target type `memory_comment`;
 * those reports land in Trust & Safety › Reports Queue, and this page is
 * where the comment itself is removed.
 *
 * Author names, thumbnails and class years are resolved for the CURRENT page
 * only (IN filters) — the bounded pattern Member Posts and the Tag Queue use.
 * Authors key on profiles.user_id, never profiles.id: the two differ in every
 * row, and an id match would name nobody.
 *
 * `?id=<uuid>` narrows the page to one comment — the Reports page links here
 * (via `memoryCommentPath`) so an admin can open the exact comment a report
 * named. A value present but not a uuid (a malformed target_id) is not
 * silently ignored: the page falls back to showing every comment AND surfaces
 * a warning Alert, so the admin knows the link didn't do what it promised.
 */

/**
 * Thin wrapper: reads `?id=` and remounts `MemoryCommentsTable` (via `key`)
 * whenever it changes or is cleared. Refine seeds a `useTable`'s filter state
 * from its permanent filters only once, on mount — without the remount, a
 * cleared `?id` would keep filtering to the stale comment.
 */
export function MemoryCommentsList() {
  const [params] = useSearchParams()
  const rawId = params.get('id')
  const commentId = parseCommentIdParam(rawId)
  const invalidId = rawId != null && rawId.trim() !== '' && commentId === null
  return <MemoryCommentsTable key={commentId ?? 'all'} commentId={commentId} invalidId={invalidId} />
}

export function MemoryCommentsTable({
  commentId,
  invalidId = false,
}: {
  commentId: string | null
  /** True when `?id=` was present but not a uuid — shows a warning instead of silently unfiltering. */
  invalidId?: boolean
}) {
  const permanent = React.useMemo<CrudFilter[]>(
    () => (commentId ? [{ field: 'id', operator: 'eq', value: commentId }] : []),
    [commentId],
  )
  const { tableProps } = useTable<MemoryComment>({
    resource: 'memory_comments',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    filters: { permanent },
  })
  const rows = (tableProps.dataSource ?? []) as MemoryComment[]

  const userIds = React.useMemo(
    () => Array.from(new Set(rows.map((c) => c.user_id).filter((v): v is string => !!v))),
    [rows],
  )
  const photoIds = React.useMemo(
    () => Array.from(new Set(rows.map((c) => c.photo_id).filter((v): v is string => !!v))),
    [rows],
  )

  const { result: profilesRes } = useList<Profile>({
    resource: 'profiles',
    filters: [{ field: 'user_id', operator: 'in', value: userIds }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: userIds.length > 0 },
  })
  const authorMap = React.useMemo<Record<string, string>>(() => {
    const items = (profilesRes?.data ?? []) as Profile[]
    return Object.fromEntries(items.filter((p) => p.user_id != null).map((p) => [p.user_id as string, p.name]))
  }, [profilesRes])

  const { result: photosRes } = useList<Photo>({
    resource: 'photos',
    filters: [{ field: 'id', operator: 'in', value: photoIds }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: photoIds.length > 0 },
  })
  const photoMap = React.useMemo<Record<string, Photo>>(() => {
    const items = (photosRes?.data ?? []) as Photo[]
    return Object.fromEntries(items.map((p) => [p.id, p]))
  }, [photosRes])

  // A memory is a photo with group_id set; its Classroom's class year labels the row.
  const groupIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(photoMap)
            .map((p) => p.group_id)
            .filter((v): v is string => !!v),
        ),
      ),
    [photoMap],
  )
  const { result: groupsRes } = useList<Group>({
    resource: 'groups',
    filters: [{ field: 'id', operator: 'in', value: groupIds }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: groupIds.length > 0 },
  })
  const yearMap = React.useMemo<Record<string, string>>(() => {
    const items = (groupsRes?.data ?? []) as Group[]
    return Object.fromEntries(items.filter((g) => g.class_year).map((g) => [g.id, g.class_year as string]))
  }, [groupsRes])

  return (
    <ListPage
      intro={{
        eyebrow: 'Trust & Safety · Feed Moderation',
        description:
          'Every comment on a Classroom memory, newest first. Delete one that breaks the community guidelines. Members can also report a comment; those reports arrive in the Reports Queue.',
      }}
    >
      <List resource="memory_comments" canCreate={false} title={false} breadcrumb={false}>
        {commentId ? (
          <Alert
            type="info"
            showIcon
            data-testid="memory-comments-single"
            style={{ marginBottom: 12, maxWidth: 720 }}
            message="Showing one comment, from a report."
            description={
              <>
                If the table is empty, the comment has already been deleted.{' '}
                <Link to="/memory_comments">Show all comments</Link>
              </>
            }
          />
        ) : invalidId ? (
          <Alert
            type="warning"
            showIcon
            data-testid="memory-comments-invalid-id"
            style={{ marginBottom: 12, maxWidth: 720 }}
            message="That link's comment id isn't valid; showing all comments."
          />
        ) : null}
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }}>
            <Table.Column<MemoryComment>
              dataIndex="body"
              title="Comment"
              render={(body: string) => (
                <span style={{ fontFamily: brand.fSans, color: brand.ink, whiteSpace: 'pre-wrap' }}>{body}</span>
              )}
            />
            <Table.Column<MemoryComment>
              dataIndex="user_id"
              title="Author"
              render={(userId: string) => renderAvatarName(authorMap[userId] ?? '—')}
            />
            <Table.Column<MemoryComment>
              dataIndex="photo_id"
              title="Memory"
              render={(photoId: string) => {
                const photo = photoMap[photoId]
                const year = photo?.group_id ? yearMap[photo.group_id] : undefined
                const classLabel = year ? `Class of ${year}` : 'Classroom'
                return (
                  <Space size={10}>
                    {photo?.url ? (
                      <Image
                        src={photo.url}
                        alt={`Memory from the ${classLabel}`}
                        width={44}
                        height={44}
                        style={{ objectFit: 'cover', borderRadius: 6 }}
                      />
                    ) : (
                      <span style={{ color: brand.sub }}>—</span>
                    )}
                    <Pill tone="jaguar">{classLabel}</Pill>
                  </Space>
                )
              }}
            />
            <Table.Column<MemoryComment>
              dataIndex="created_at"
              title="Posted"
              render={(value: string) => <DateField value={value} format="MMM D, YYYY h:mm A" />}
            />
            <Table.Column<MemoryComment>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: MemoryComment) => (
                <DeleteButton
                  hideText
                  size="small"
                  resource="memory_comments"
                  recordItemId={record.id}
                  data-testid={`memory-comments-delete-${record.id}`}
                  confirmTitle="Delete this comment?"
                  confirmOkText="Delete"
                />
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
